import { type FunctionComponent } from 'react'
import React from 'react'
import styled from '@emotion/styled'
import {
  typography,
  ItemsList,
  Divider,
  Flex,
  Text,
  spacing,
} from '@retailer-platform/shared-components/src/tds'
import { usePartnerContext } from '@retailer-platform/dashboard/utils'
import { useDomainMessages } from '../../../../utils/domain/intl'
import { COLUMN_WIDTHS } from '../types'
import { type NotificationsNotificationChannel } from '../../../../api'
import { useIsInAppEnabled } from '../hooks/useIsInAppEnabled.hooks'
import { NotificationSettingRow } from './NotificationSettingRow'

const ColumnHeader = styled.div({
  ...typography.bodyEmphasized,
})

const SectionDescription = styled.div(({ theme }) => ({
  marginBottom: '24px',
  color: theme.colors.systemGrayscale80,
}))

const InfoMessage = styled.div(({ theme }) => ({
  color: theme.colors.systemGrayscale80,
  marginBottom: '16px',
}))

const PlatformHeaderWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
})

export const PlatformChannelsSection: FunctionComponent<{
  platformChannels: NotificationsNotificationChannel[]
}> = ({ platformChannels }) => {
  const partnerContext = usePartnerContext()

  const i18n = useDomainMessages({
    platformTitle: 'notificationsDomain.list.globalSettings.pageTitle',
    platformDesc: 'notificationsDomain.list.globalSettings.pageDescription',
    eventName: 'notificationsDomain.list.columnTitle.eventName',
    description: 'notificationsDomain.list.columnTitle.description',
    partner: 'notificationsDomain.list.columnTitle.partner',
    email: 'notificationsDomain.list.columnTitle.email',
    inApp: 'notificationsDomain.list.columnTitle.inApp',
    noNotifications: 'notificationsDomain.list.noNotifications',
  })

  const isInAppEnabled = useIsInAppEnabled()

  return (
    <>
      <PlatformHeaderWrapper>
        <Text typography="title" css={{ marginBottom: '8px' }}>
          {i18n.platformTitle}
        </Text>
        <SectionDescription>
          <Text typography="bodyRegular">{i18n.platformDesc}</Text>
        </SectionDescription>
      </PlatformHeaderWrapper>
      {platformChannels.length === 0 ? (
        <InfoMessage>{i18n.noNotifications}</InfoMessage>
      ) : (
        <>
          <ItemsList.Section>
            <Flex.Row gap={spacing.s8} alignItems="center">
              <Flex.Column css={{ width: COLUMN_WIDTHS.EVENT_NAME }}>
                <ColumnHeader>{i18n.eventName}</ColumnHeader>
              </Flex.Column>
              <Flex.Column css={{ width: COLUMN_WIDTHS.DESCRIPTION }}>
                <ColumnHeader>{i18n.description}</ColumnHeader>
              </Flex.Column>
              <Flex.Column css={{ width: COLUMN_WIDTHS.PARTNER }}></Flex.Column>
              <Flex.Column css={{ width: COLUMN_WIDTHS.EMAIL }}>
                <ColumnHeader>{i18n.email}</ColumnHeader>
              </Flex.Column>
              {isInAppEnabled && (
                <Flex.Column css={{ width: COLUMN_WIDTHS.IN_APP }}>
                  <ColumnHeader>{i18n.inApp}</ColumnHeader>
                </Flex.Column>
              )}
              <Flex.Column css={{ width: COLUMN_WIDTHS.BANNER }}>
                {/*no banners for platform notifications, no need to name the column but keeping it for spacing*/}
                <ColumnHeader></ColumnHeader>
              </Flex.Column>
            </Flex.Row>
          </ItemsList.Section>
          <Divider />
          {platformChannels.map((notification, index) => (
            <React.Fragment key={`platform-${notification.id}`}>
              <NotificationSettingRow channel={notification} showInApp={isInAppEnabled} />
              {index < platformChannels.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </>
      )}
    </>
  )
}
